import { lazy, Suspense, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ErrorBoundary } from '@aia/ui-components';

import { ModuleGuard } from '@/components/auth/ModuleGuard';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { ModulesContext, type EnabledModules } from '@/hooks/useModules';
import { useAuth } from '@/lib/auth';
import { trackException } from '@/lib/telemetry';

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]" role="status">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <span className="sr-only">Loading page...</span>
    </div>
  </div>
);

// Public pages
const LandingPage = lazy(() =>
  import('@/features/auth/pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const AcceptInvitePage = lazy(() =>
  import('@/features/auth/pages/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const SsoLandingPage = lazy(() =>
  import('@/features/auth/pages/SsoLandingPage').then((m) => ({ default: m.SsoLandingPage })),
);

// Dashboard pages
const DashboardHome = lazy(() =>
  import('@/features/dashboard/pages/DashboardHome').then((m) => ({ default: m.DashboardHome })),
);
const PilotsList = lazy(() =>
  import('@/features/pilots/pages/PilotsList').then((m) => ({ default: m.PilotsList })),
);
const PilotDetail = lazy(() =>
  import('@/features/pilots/pages/PilotDetail').then((m) => ({ default: m.PilotDetail })),
);
const CreatePilot = lazy(() =>
  import('@/features/pilots/pages/CreatePilot').then((m) => ({ default: m.CreatePilot })),
);
const WeeklyLogForm = lazy(() =>
  import('@/features/weekly-logs/pages/WeeklyLogForm').then((m) => ({ default: m.WeeklyLogForm })),
);
const WeeklyLogsList = lazy(() =>
  import('@/features/weekly-logs/pages/WeeklyLogsList').then((m) => ({
    default: m.WeeklyLogsList,
  })),
);
const WalkthroughForm = lazy(() =>
  import('@/features/walkthroughs/pages/WalkthroughForm').then((m) => ({
    default: m.WalkthroughForm,
  })),
);
const WalkthroughsList = lazy(() =>
  import('@/features/walkthroughs/pages/WalkthroughsList').then((m) => ({
    default: m.WalkthroughsList,
  })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const OrganizationStructure = lazy(() =>
  import('@/features/organization/pages/OrganizationStructure').then((m) => ({
    default: m.OrganizationStructure,
  })),
);
const TeamManagementPage = lazy(() =>
  import('@/features/organization/pages/TeamManagementPage').then((m) => ({
    default: m.TeamManagementPage,
  })),
);
const GuidePage = lazy(() =>
  import('@/features/guide/pages/GuidePage').then((m) => ({ default: m.GuidePage })),
);
const ModuleNotAvailable = lazy(() =>
  import('@/features/shared/pages/ModuleNotAvailable').then((m) => ({
    default: m.ModuleNotAvailable,
  })),
);

// Payment pages
const PaymentSuccessPage = lazy(() =>
  import('@/features/payment/pages/PaymentSuccessPage').then((m) => ({
    default: m.PaymentSuccessPage,
  })),
);
const PaymentCancelledPage = lazy(() =>
  import('@/features/payment/pages/PaymentCancelledPage').then((m) => ({
    default: m.PaymentCancelledPage,
  })),
);

// Shared pages
const SharedReportViewer = lazy(() =>
  import('@/features/shared/pages/SharedReportViewer').then((m) => ({
    default: m.SharedReportViewer,
  })),
);

const DEFAULT_MODULES: EnabledModules = {
  pilotPulse: false,
  progressPulse: false,
  insightPulse: false,
};

function App() {
  const { user } = useAuth();

  const enabledModules = useMemo<EnabledModules>(() => {
    // The /auth/me endpoint returns enabledModules as string[] (e.g. ["pilot","progress","insight"])
    // while ModulesContext expects an object keyed by ModuleName. Normalize both shapes.
    if (Array.isArray(user?.enabledModules)) {
      return {
        pilotPulse: user.enabledModules.includes('pilot'),
        progressPulse: user.enabledModules.includes('progress'),
        insightPulse: user.enabledModules.includes('insight'),
      };
    }
    if (user?.enabledModules) return user.enabledModules;
    if (user?.isPlatformAdmin) return { pilotPulse: true, progressPulse: true, insightPulse: true };
    return DEFAULT_MODULES;
  }, [user]);

  return (
    <ModulesContext.Provider value={enabledModules}>
      <ErrorBoundary
        onError={(error, errorInfo) => {
          trackException(error, {
            type: 'REACT_ERROR_BOUNDARY',
            componentStack: errorInfo.componentStack || 'unknown',
          });
        }}
      >
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>

        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/register" element={<Navigate to="/" replace />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/sso" element={<SsoLandingPage />} />
            </Route>

            {/* Dashboard routes (authenticated) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />

              {/* Module unavailable page */}
              <Route path="module-unavailable" element={<ModuleNotAvailable />} />

              {/* Pilots - guarded by pilotPulse module */}
              <Route
                path="pilots"
                element={
                  <ModuleGuard module="pilotPulse">
                    <PilotsList />
                  </ModuleGuard>
                }
              />
              <Route
                path="pilots/new"
                element={
                  <ModuleGuard module="pilotPulse">
                    <CreatePilot />
                  </ModuleGuard>
                }
              />
              <Route
                path="pilots/:id"
                element={
                  <ModuleGuard module="pilotPulse">
                    <PilotDetail />
                  </ModuleGuard>
                }
              />

              {/* Weekly Logs - part of pilotPulse */}
              <Route
                path="weekly-logs"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WeeklyLogsList />
                  </ModuleGuard>
                }
              />
              <Route
                path="weekly-logs/:pilotId/new"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WeeklyLogForm />
                  </ModuleGuard>
                }
              />
              <Route
                path="weekly-logs/:pilotId/:weekNumber"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WeeklyLogForm />
                  </ModuleGuard>
                }
              />

              {/* Walkthroughs - part of pilotPulse */}
              <Route
                path="walkthroughs"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WalkthroughsList />
                  </ModuleGuard>
                }
              />
              <Route
                path="walkthroughs/:pilotId/new"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WalkthroughForm />
                  </ModuleGuard>
                }
              />
              <Route
                path="walkthroughs/:id"
                element={
                  <ModuleGuard module="pilotPulse">
                    <WalkthroughForm />
                  </ModuleGuard>
                }
              />

              {/* Reports */}
              <Route path="reports" element={<ReportsPage />} />

              {/* Administrator Guide */}
              <Route path="guide" element={<GuidePage />} />

              {/* Organization Structure (admin-only) */}
              <Route
                path="organization/structure"
                element={
                  <ProtectedRoute roles={['OrgAdmin', 'UnitAdmin', 'PlatformAdmin']}>
                    <OrganizationStructure />
                  </ProtectedRoute>
                }
              />
              <Route
                path="organization/team"
                element={
                  <ProtectedRoute roles={['OrgAdmin', 'UnitAdmin', 'PlatformAdmin']}>
                    <TeamManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* Settings */}
              <Route path="settings/*" element={<SettingsPage />} />
            </Route>

            {/* Payment result pages */}
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute>
                  <PaymentSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/cancelled"
              element={
                <ProtectedRoute>
                  <PaymentCancelledPage />
                </ProtectedRoute>
              }
            />

            {/* Shared report viewer (public, no auth) */}
            <Route path="/shared/report/:shareToken" element={<SharedReportViewer />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </ModulesContext.Provider>
  );
}

export default App;
